import React from 'react';
import { Download, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from '@/hooks/use-toast';
import { Database } from '@/integrations/supabase/types';

type CustomerProfile = Database['public']['Tables']['customer_profiles']['Row'];

interface ExportCSVButtonProps {
  className?: string;
  filteredUsers?: CustomerProfile[];
}

const ExportCSVButton: React.FC<ExportCSVButtonProps> = ({ className, filteredUsers }) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const exportUsers = async () => {
    try {
      toast({
        title: t('admin.exportingData'),
        description: t('admin.preparingDownload'),
      });

      let usersToExport: CustomerProfile[] = [];

      // If filtered users are provided, use them
      if (filteredUsers && filteredUsers.length > 0) {
        usersToExport = filteredUsers;
      } else {
        // Otherwise fetch all users from the database
        const { data, error } = await supabase
          .from('customer_profiles')
          .select('*');

        if (error) throw error;
        usersToExport = data || [];
      }

      // Generate CSV from data
      const csvContent = generateCSV(usersToExport);

      // Create download
      downloadCSV(csvContent, 'users_export.csv');

      toast({
        title: t('admin.exportComplete'),
        description: t('admin.fileDownloaded'),
      });
    } catch (error) {
      console.error('Error exporting users:', error);
      toast({
        title: t('admin.exportError'),
        description: t('admin.errorExportingData'),
        variant: 'destructive',
      });
    }
  };

  const generateCSV = (data: CustomerProfile[]) => {
    // Define headers
    const headers = [
      'id', 
      'email', 
      'first_name', 
      'last_name', 
      'phone', 
      'address',
      'city',
      'postal_code',
      'points_balance',
      'tier',
      'created_at'
    ];

    // Create CSV header row
    const csvRows = [
      headers.join(',')
    ];

    // Add data rows
    data.forEach(user => {
      const values = headers.map(header => {
        const value = user[header as keyof CustomerProfile];
        // Handle CSV special characters by wrapping in quotes
        return value === null ? '' : `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    });

    return csvRows.join('\n');
  };

  const downloadCSV = (csvContent: string, fileName: string) => {
    // Create a blob with the CSV data
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create a link element and trigger download
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Button 
      variant="custom-red" 
      className={`${className} border border-white`} 
      onClick={exportUsers}
    >
      <FileSpreadsheet className="mr-2 h-4 w-4" />
      {t('admin.exportCSV')}
    </Button>
  );
};

export default ExportCSVButton;
